import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";

const RadiologyReports = ({ patientId }) => {
  const [radiologyReports, setRadiologyReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const authToken = sessionStorage.getItem("accessToken") || "";
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
    }),
    [authToken]
  );

  useEffect(() => {
    const fetchRadiologyReports = async () => {
      if (!patientId) {
        setRadiologyReports([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}telerad/patient_dicom_records/${patientId}/`,
          { headers }
        );

        const payload = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setRadiologyReports(payload);
      } catch (error) {
        console.error("Error fetching radiology reports:", error);
        toast.error(
          error?.response?.data?.message ||
            "Unable to fetch radiology reports at the moment."
        );
        setRadiologyReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRadiologyReports();
  }, [patientId, REACT_APP_BACKEND_ROUTE_URL, headers]);

  const rows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return radiologyReports
      .map((item, index) => ({
        id:
          item?.id ||
          `${item?.carecontext_id || "cc"}-${item?.medical_service_id || "ms"}-${
            item?.registration_date || index
          }-${index}`,
        ...item,
      }))
      .filter((row) => {
        if (!normalizedSearch) return true;
        return (
          String(row?.carecontext_id || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.medical_service_id || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.medical_service_name || "")
            .toLowerCase()
            .includes(normalizedSearch) ||
          String(row?.registration_date || "")
            .toLowerCase()
            .includes(normalizedSearch)
        );
      });
  }, [radiologyReports, searchText]);

  // The TeleRad viewer sends `frame-ancestors 'self'` and a SameSite=Strict
  // login cookie, so browsers refuse to render it inside an iframe on our
  // origin. It has to open in its own tab.
  const openViewer = useCallback((dicomUrl) => {
    if (!dicomUrl) {
      toast.info("Radiology report is not available yet.");
      return;
    }
    window.open(dicomUrl, "_blank", "noopener,noreferrer");
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "carecontext_id",
        headerName: "Care context",
        minWidth: 180,
        flex: 1,
        sortable: true,
      },
      {
        field: "medical_service_name",
        headerName: "Medical Service Name",
        minWidth: 220,
        flex: 1,
        sortable: true,
      },
      {
        field: "registration_date",
        headerName: "Date",
        minWidth: 160,
        flex: 1,
        sortable: true,
        valueFormatter: (params) => {
          if (!params?.value) return "-";
          return params.value;
        },
      },
      {
        field: "view",
        headerName: "View",
        minWidth: 140,
        flex: 0.5,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Typography
            component="span"
            onClick={() =>
              openViewer(params?.row?.diacom_url || params?.row?.dicom_url)
            }
            sx={{
              color: "#176BAD",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            Click to View
          </Typography>
        ),
      },
    ],
    [openViewer]
  );

  return (
    <Box sx={{ pt: 1 }}>
      <Paper
        sx={{
          border: "1px solid #176BAD",
          borderRadius: "10px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: 1.5,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by care context, medical service or date"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[5, 10, 20]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: "registration_date", sort: "desc" }],
              },
            }}
            disableRowSelectionOnClick
            sx={{
              "&.MuiDataGrid-root": {
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                fontFamily: "Arial, sans-serif",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#125386",
                color: "#ffffff",
                borderBottom: "1px solid #D9D9D9",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "13px",
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "rgba(255,255,255,0.35)",
              },
              "& .MuiSvgIcon-root": {
                color: "#ffffff",
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid #e0e0e0",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
              },
              "& .MuiDataGrid-cell": {
                fontSize: "12px",
                color: "#374151",
                borderRight: "1px solid #e0e0e0",
                "&:last-child": {
                  borderRight: "none",
                },
                "&:focus": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default RadiologyReports;
